import type {
  ApiError,
  ApiInstance,
  ApiRequestConfig,
  EloquentModel,
  LaravelApiResponse,
  LaravelPaginatedResponse,
  QueryBuilder,
} from '~/types/plugins/api';

// Request deduplication system
interface RequestCacheEntry<T = unknown> {
  promise: Promise<T>;
  timestamp: number;
}

// Global in-flight request registry, keyed by method+url+params+data. Despite
// the name, this is NOT a response cache — `deduplicateRequest` below removes
// an entry the moment its request settles (success or failure), so a second
// call for the same key one tick later starts a brand new request rather than
// reusing the previous result. It only collapses calls that are concurrently
// in flight for the same key; the `ttl`/`DEFAULT_CACHE_TTL` naming suggests a
// longer-lived cache, but nothing here is ever served stale past its own
// in-flight window.
const requestCache = new Map<string, RequestCacheEntry>();

// Only meaningful as an upper bound on how long an in-flight entry can be
// deduped against before `cleanExpiredCacheEntries` sweeps it — NOT a
// "how long is the response cached for" window (see the note on
// `requestCache` above; there is no post-completion cache to expire).
const DEFAULT_CACHE_TTL = 5000;

/**
 * Creates a cache key for request deduplication
 */
function createRequestCacheKey(
  method: string,
  url: string,
  params?: Record<string, string | number | boolean>,
  data?: unknown,
): string {
  const paramString = params ? JSON.stringify(params) : '';
  const dataString = data ? JSON.stringify(data) : '';
  return `${method.toUpperCase()}:${url}:${paramString}:${dataString}`;
}

/**
 * Cleans up expired cache entries
 */
function cleanExpiredCacheEntries(ttl: number = DEFAULT_CACHE_TTL): void {
  const now = Date.now();
  for (const [key, entry] of requestCache.entries()) {
    if (now - entry.timestamp > ttl) {
      requestCache.delete(key);
    }
  }
}

/**
 * Deduplicates requests by caching ongoing promises
 */
function deduplicateRequest<T>(
  cacheKey: string,
  requestFn: () => Promise<T>,
  ttl: number = DEFAULT_CACHE_TTL,
): Promise<T> {
  // Never deduplicate on the server. `requestCache` is module scope, so under
  // SSR it is one Map shared by every concurrent request in the Node process,
  // and the key is method+url+params with no per-visitor dimension — so one
  // visitor's in-flight response can be handed to another. That is harmless
  // while every endpoint is public and anonymous, but it is a data-leak waiting
  // for the first authenticated or personalised endpoint.
  //
  // Dedup exists to collapse duplicate calls from components that mount twice
  // on the client; a single server render has no such duplication to collapse,
  // so skipping it here costs nothing.
  if (import.meta.server) return requestFn();

  // Clean up expired entries periodically
  if (requestCache.size > 100) {
    cleanExpiredCacheEntries(ttl);
  }

  // Check if request is already in progress
  const existingEntry = requestCache.get(cacheKey);
  if (existingEntry) {
    return existingEntry.promise as Promise<T>;
  }

  // Create new request
  const promise = requestFn().finally(() => {
    // Remove from cache when request completes (success or failure)
    requestCache.delete(cacheKey);
  });

  // Cache the promise
  requestCache.set(cacheKey, {
    promise,
    timestamp: Date.now(),
  });

  return promise;
}

/**
 * Standardizes response format to match LaravelPaginatedResponse pattern
 */
function standardizeResponse<T>(
  response: LaravelPaginatedResponse<T> | T[],
  pending = false,
): T[] | LaravelPaginatedResponse<T> {
  if ('data' in response && Array.isArray(response.data)) {
    return {
      data: response.data,
      meta: response.meta ?? {},
      pending,
    };
  }
  return response as T[];
}

/**
 * Query Builder implementation for Eloquent-like queries
 */
class ResourceQueryBuilder<T> implements QueryBuilder<T> {
  private params: Record<string, unknown> = {};
  private api: ApiInstance;
  private resourceUrl: string;
  private enableDeduplication: boolean;
  private deduplicationTtl: number;

  constructor(
    api: ApiInstance,
    resourceUrl: string,
    enableDeduplication = true,
    deduplicationTtl = DEFAULT_CACHE_TTL,
  ) {
    this.api = api;
    this.resourceUrl = resourceUrl;
    this.enableDeduplication = enableDeduplication;
    this.deduplicationTtl = deduplicationTtl;
  }

  where(
    field: string,
    operator: string | unknown,
    value?: unknown,
  ): QueryBuilder<T> {
    if (value === undefined) {
      // where(field, value) - operator is '='
      this.params[`filter[${field}]`] = operator;
    } else {
      // where(field, operator, value)
      this.params[`filter[${field}][${operator}]`] = value;
    }
    return this;
  }

  whereIn(field: string, values: unknown[]): QueryBuilder<T> {
    this.params[`filter[${field}][in]`] = values.join(',');
    return this;
  }

  whereNotIn(field: string, values: unknown[]): QueryBuilder<T> {
    this.params[`filter[${field}][not_in]`] = values.join(',');
    return this;
  }

  whereNull(field: string): QueryBuilder<T> {
    this.params[`filter[${field}][null]`] = true;
    return this;
  }

  whereNotNull(field: string): QueryBuilder<T> {
    this.params[`filter[${field}][not_null]`] = true;
    return this;
  }

  whereBetween(field: string, values: [unknown, unknown]): QueryBuilder<T> {
    this.params[`filter[${field}][between]`] = values.join(',');
    return this;
  }

  orderBy(field: string, direction: 'asc' | 'desc' = 'asc'): QueryBuilder<T> {
    this.params.sort = `${direction === 'desc' ? '-' : ''}${field}`;
    return this;
  }

  orderByDesc(field: string): QueryBuilder<T> {
    return this.orderBy(field, 'desc');
  }

  limit(count: number): QueryBuilder<T> {
    this.params.limit = count;
    return this;
  }

  offset(count: number): QueryBuilder<T> {
    this.params.offset = count;
    return this;
  }

  with(relations: string | string[]): QueryBuilder<T> {
    const relationString = Array.isArray(relations)
      ? relations.join(',')
      : relations;
    this.params.include = relationString;
    return this;
  }

  select(fields: string | string[]): QueryBuilder<T> {
    const fieldString = Array.isArray(fields) ? fields.join(',') : fields;
    this.params.fields = fieldString;
    return this;
  }

  async get(): Promise<T[] | LaravelPaginatedResponse<T>> {
    const params = this.params as Record<string, string | number | boolean>;

    const pending = ref(true);
    const requestFn = async () =>
      await this.api.get<LaravelPaginatedResponse<T> | T[]>(this.resourceUrl, {
        params,
      });
    pending.value = false;

    let response: LaravelPaginatedResponse<T> | T[];

    if (this.enableDeduplication) {
      const cacheKey = createRequestCacheKey('GET', this.resourceUrl, params);
      response = await deduplicateRequest(
        cacheKey,
        requestFn,
        this.deduplicationTtl,
      );
    } else {
      response = await requestFn();
    }

    return standardizeResponse(response, pending.value);
  }

  async first(): Promise<T | null> {
    this.limit(1);
    const results = await this.get();

    if (Array.isArray(results)) {
      return results.length > 0 ? results[0] || null : null;
    } else if ('data' in results && Array.isArray(results.data)) {
      return results.data.length > 0 ? results.data[0] || null : null;
    }

    return null;
  }

  async paginate(
    perPage = 15,
    page = 1,
  ): Promise<T[] | LaravelPaginatedResponse<T>> {
    this.params.per_page = perPage;
    this.params.page = page;

    const params = this.params as Record<string, string | number | boolean>;

    const pending = ref(true);
    const requestFn = async () =>
      await this.api.get<LaravelPaginatedResponse<T> | T[]>(this.resourceUrl, {
        params,
      });
    pending.value = false;

    let response: LaravelPaginatedResponse<T> | T[];

    if (this.enableDeduplication) {
      const cacheKey = createRequestCacheKey('GET', this.resourceUrl, params);
      response = await deduplicateRequest(
        cacheKey,
        requestFn,
        this.deduplicationTtl,
      );
    } else {
      response = await requestFn();
    }

    return standardizeResponse(response, pending.value);
  }

  async count(): Promise<number> {
    this.params.count_only = true;
    const params = this.params as Record<string, string | number | boolean>;

    const requestFn = () =>
      this.api.get<{ count: number }>(this.resourceUrl, { params });

    let response: { count: number };

    if (this.enableDeduplication) {
      const cacheKey = createRequestCacheKey('GET', this.resourceUrl, params);
      response = await deduplicateRequest(
        cacheKey,
        requestFn,
        this.deduplicationTtl,
      );
    } else {
      response = await requestFn();
    }

    return response.count;
  }

  async exists(): Promise<boolean> {
    const count = await this.count();
    return count > 0;
  }

  toSql(): string {
    // Generate a SQL-like query string for debugging
    const conditions: string[] = [];

    Object.entries(this.params).forEach(([key, value]) => {
      if (key.startsWith('filter[')) {
        const field = key.match(/filter\[([^\]]+)\]/)?.[1];
        if (field) {
          conditions.push(`${field} = ${value}`);
        }
      }
    });

    let sql = `SELECT * FROM ${this.resourceUrl.replace('/', '')}`;
    if (conditions.length > 0) {
      sql += ` WHERE ${conditions.join(' AND ')}`;
    }

    if (this.params.sort) {
      const sort = this.params.sort as string;
      const direction = sort.startsWith('-') ? 'DESC' : 'ASC';
      const field = sort.replace('-', '');
      sql += ` ORDER BY ${field} ${direction}`;
    }

    if (this.params.limit) {
      sql += ` LIMIT ${this.params.limit}`;
    }

    return sql;
  }
}

// Helper function to create API errors
function createApiError(
  message: string,
  status?: number,
  statusText?: string,
  data?: unknown,
  config?: unknown,
): ApiError {
  const error = new Error(message) as ApiError;
  error.status = status;
  error.statusText = statusText;
  error.data = data;
  error.config = config as ApiRequestConfig;
  return error;
}

/**
 * Creates an Eloquent-like model with Laravel ORM methods and reactive capabilities
 */
export function useApiModel<T = Record<string, unknown>>(
  resourceName: string,
  options: {
    baseUrl?: string;
    immediate?: boolean;
    filters?: MaybeRefOrGetter<Record<string, string | number | boolean>>;
    page?: MaybeRefOrGetter<number>;
    limit?: MaybeRefOrGetter<number>;
    autoLoad?: boolean;
    enableDeduplication?: boolean;
    deduplicationTtl?: number;
  } = {},
): EloquentModel<T> & {
  // Reactive state
  items: Readonly<Ref<T[]>>;
  meta: Readonly<
    Ref<{
      current_page?: number;
      last_page?: number;
      total?: number;
      per_page?: number;
    }>
  >;
  // Reactive methods
  load: () => Promise<void>;
  refresh: () => Promise<void>;
} {
  const { $apiClient } = useNuxtApp() as unknown as { $apiClient: ApiInstance };

  const {
    baseUrl,
    immediate = false,
    filters,
    page,
    limit,
    autoLoad = true,
    enableDeduplication = true,
    deduplicationTtl = DEFAULT_CACHE_TTL,
  } = options;

  const pending = ref(false);
  const error = ref<ApiError | null>(null);

  // Normalize resource URL to handle trailing slashes
  const resourceUrl = baseUrl
    ? (baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl) +
      (resourceName.startsWith('/') ? resourceName : `/${resourceName}`)
    : `/${resourceName}`;

  // Reactive state for list data
  const items = ref<T[]>([]);
  const meta = ref<{
    current_page?: number;
    last_page?: number;
    total?: number;
    per_page?: number;
  }>({});

  const getFilters = () => (filters ? toValue(filters) : {});
  const getPage = () => (page ? toValue(page) : 1);
  const getLimit = () => (limit ? toValue(limit) : 15);

  const handleRequest = async <R>(
    requestFn: () => Promise<R>,
    method?: string,
    url?: string,
    params?: Record<string, string | number | boolean>,
    data?: unknown,
  ): Promise<R> => {
    pending.value = true;
    error.value = null;

    try {
      // Use deduplication if enabled and we have the required parameters
      if (enableDeduplication && method && url) {
        const cacheKey = createRequestCacheKey(method, url, params, data);
        const result = await deduplicateRequest(
          cacheKey,
          requestFn,
          deduplicationTtl,
        );
        return result;
      } else {
        const result = await requestFn();
        return result;
      }
    } catch (err) {
      error.value = err as ApiError;
      throw err;
    } finally {
      pending.value = false;
    }
  };

  // Reactive load function
  const load = async (): Promise<void> => {
    try {
      const params = {
        ...getFilters(),
        page: getPage(),
        per_page: getLimit(),
      };

      // Passing method/url/params through (unlike the original, which called
      // `handleRequest` with none of them) is what actually lets `load()`
      // participate in `handleRequest`'s dedup gate below — without them it
      // always took the "no dedup" branch regardless of `enableDeduplication`,
      // silently, for every reactive list load in the app.
      const response = await handleRequest(
        async () =>
          await $apiClient.get<LaravelPaginatedResponse<T> | T[]>(resourceUrl, {
            params,
          }),
        'GET',
        resourceUrl,
        params as Record<string, string | number | boolean>,
      );

      if ('data' in response && Array.isArray(response.data)) {
        // Paginated response
        items.value = response.data;
        meta.value = {
          current_page: response.meta?.current_page,
          last_page: response.meta?.last_page,
          total: response.meta?.total,
          per_page: response.meta?.per_page,
        };
      } else if (Array.isArray(response)) {
        // Simple array response
        items.value = response;
        meta.value = { total: response.length };
      }
    } catch (err) {
      console.error(`Failed to load ${resourceName}:`, err);
    }
  };

  const refresh = load;

  // Cleanup function for watchers
  const stopWatchers: (() => void)[] = [];

  // Watch for changes in reactive parameters
  if (autoLoad) {
    // Watch filters for changes
    if (filters) {
      const stopFilterWatch = watch(() => getFilters(), load, { deep: true });
      stopWatchers.push(stopFilterWatch);
    }

    // Watch page changes
    if (page) {
      const stopPageWatch = watch(() => getPage(), load);
      stopWatchers.push(stopPageWatch);
    }

    // Watch limit changes
    if (limit) {
      const stopLimitWatch = watch(() => getLimit(), load);
      stopWatchers.push(stopLimitWatch);
    }

    // Load initial data
    if (immediate) {
      load();
    }
  }

  // Cleanup watchers on scope disposal
  onScopeDispose(() => {
    stopWatchers.forEach((stop) => stop());
  });

  // Create a new query builder instance
  const createQueryBuilder = (): QueryBuilder<T> => {
    return new ResourceQueryBuilder<T>(
      $apiClient,
      resourceUrl,
      enableDeduplication,
      deduplicationTtl,
    );
  };

  return {
    // Reactive state
    items: readonly(items) as Readonly<Ref<T[]>>,
    meta: readonly(meta),
    pending: readonly(pending),
    error: readonly(error),

    // Reactive methods
    load,
    refresh,

    // Basic ResourceApi methods (inherited)
    async index(
      params?: Record<string, string | number | boolean>,
    ): Promise<LaravelPaginatedResponse<T> | T[]> {
      const response = await handleRequest(
        async () =>
          await $apiClient.get<LaravelPaginatedResponse<T> | T[]>(resourceUrl, {
            params,
          }),
        'GET',
        resourceUrl,
        params,
      );
      return standardizeResponse(response);
    },

    async show(id: string | number): Promise<LaravelApiResponse<T>> {
      const url = `${resourceUrl}/${id}`;
      return handleRequest(
        async () => await $apiClient.get<LaravelApiResponse<T>>(url),
        'GET',
        url,
      );
    },

    async store(data: Partial<T>): Promise<LaravelApiResponse<T>> {
      return handleRequest(
        async () =>
          await $apiClient.post<LaravelApiResponse<T>>(resourceUrl, data),
        'POST',
        resourceUrl,
        undefined,
        data,
      );
    },

    async update(
      id: string | number,
      data: Partial<T>,
    ): Promise<LaravelApiResponse<T>> {
      const url = `${resourceUrl}/${id}`;
      return handleRequest(
        async () => await $apiClient.put<LaravelApiResponse<T>>(url, data),
        'PUT',
        url,
        undefined,
        data,
      );
    },

    async destroy(id: string | number): Promise<LaravelApiResponse<null>> {
      const url = `${resourceUrl}/${id}`;
      return handleRequest(
        async () => await $apiClient.delete<LaravelApiResponse<null>>(url),
        'DELETE',
        url,
      );
    },

    // Query builder methods
    query(): QueryBuilder<T> {
      return createQueryBuilder();
    },

    where(
      field: string,
      operator: string | unknown,
      value?: unknown,
    ): QueryBuilder<T> {
      return createQueryBuilder().where(field, operator, value);
    },

    whereIn(field: string, values: unknown[]): QueryBuilder<T> {
      return createQueryBuilder().whereIn(field, values);
    },

    orderBy(field: string, direction: 'asc' | 'desc' = 'asc'): QueryBuilder<T> {
      return createQueryBuilder().orderBy(field, direction);
    },

    // Eloquent finder methods
    async all(
      params?: Record<string, unknown>,
    ): Promise<T[] | LaravelPaginatedResponse<T>> {
      const apiParams = params as Record<string, string | number | boolean>;
      const response = await handleRequest(
        async () => {
          const response = await $apiClient.get<
            LaravelPaginatedResponse<T> | T[]
          >(resourceUrl, { params: apiParams });
          return response;
        },
        'GET',
        resourceUrl,
        apiParams,
      );

      return standardizeResponse(response, pending.value);
    },

    async find(id: string | number): Promise<T | null> {
      const url = `${resourceUrl}/${id}`;
      try {
        const response = await handleRequest(
          async () => await $apiClient.get<LaravelApiResponse<T>>(url),
          'GET',
          url,
        );
        return response.data;
      } catch (err) {
        // Return null for 404 errors, throw for other errors
        if ((err as ApiError).status === 404) {
          return null;
        }
        throw err;
      }
    },

    async findOrFail(id: string | number): Promise<T> {
      const url = `${resourceUrl}/${id}`;
      const response = await handleRequest(
        async () => await $apiClient.get<LaravelApiResponse<T>>(url),
        'GET',
        url,
      );
      return response.data;
    },

    async findMany(ids: (string | number)[]): Promise<T[]> {
      const params = { 'filter[id][in]': ids.join(',') };
      const response = await handleRequest(
        async () => {
          const response = await $apiClient.get<
            LaravelPaginatedResponse<T> | T[]
          >(resourceUrl, { params });
          return response;
        },
        'GET',
        resourceUrl,
        params,
      );

      return standardizeResponse(response, pending.value) as T[];
    },

    async first(params?: Record<string, unknown>): Promise<T | null> {
      const queryBuilder = createQueryBuilder();

      // Apply filters if provided
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          queryBuilder.where(key, value);
        });
      }

      return queryBuilder.limit(1).first();
    },

    async firstOrFail(params?: Record<string, unknown>): Promise<T> {
      const result = await this.first(params);
      if (!result) {
        throw createApiError('No results found', 404, 'Not Found');
      }
      return result;
    },

    // Eloquent create/update methods
    async create(data: Partial<T>): Promise<T> {
      const response = await handleRequest(
        async () =>
          await $apiClient.post<LaravelApiResponse<T>>(resourceUrl, data),
        'POST',
        resourceUrl,
        undefined,
        data,
      );
      return response.data;
    },

    // Eloquent delete methods
    async delete(id: string | number): Promise<boolean> {
      const url = `${resourceUrl}/${id}`;
      try {
        await handleRequest(
          async () => await $apiClient.delete(url),
          'DELETE',
          url,
        );
        return true;
      } catch {
        return false;
      }
    },

    // Pagination methods
    async paginate(
      perPage = 15,
      page = 1,
    ): Promise<LaravelPaginatedResponse<T>> {
      const params = { per_page: perPage, page };
      const response = await handleRequest(
        async () =>
          await $apiClient.get<LaravelPaginatedResponse<T>>(resourceUrl, {
            params,
          }),
        'GET',
        resourceUrl,
        params,
      );

      return standardizeResponse(
        response,
        pending.value,
      ) as LaravelPaginatedResponse<T>;
    },

    async simplePaginate(
      perPage = 15,
      page = 1,
    ): Promise<LaravelPaginatedResponse<T>> {
      const params = { per_page: perPage, page, simple: true };
      const response = await handleRequest(
        async () =>
          await $apiClient.get<LaravelPaginatedResponse<T>>(resourceUrl, {
            params,
          }),
        'GET',
        resourceUrl,
        params,
      );

      return standardizeResponse(
        response,
        pending.value,
      ) as LaravelPaginatedResponse<T>;
    },

    // Aggregation methods
    async count(params?: Record<string, unknown>): Promise<number> {
      const apiParams = { ...params, count_only: true } as Record<
        string,
        string | number | boolean
      >;
      return handleRequest(
        async () => {
          const response = await $apiClient.get<{ count: number }>(
            resourceUrl,
            {
              params: apiParams,
            },
          );
          return response.count;
        },
        'GET',
        resourceUrl,
        apiParams,
      );
    },

    async max(
      field: string,
      params?: Record<string, unknown>,
    ): Promise<number> {
      const apiParams = { ...params, aggregate: 'max', field } as Record<
        string,
        string | number | boolean
      >;
      return handleRequest(
        async () => {
          const response = await $apiClient.get<{ max: number }>(resourceUrl, {
            params: apiParams,
          });
          return response.max;
        },
        'GET',
        resourceUrl,
        apiParams,
      );
    },

    async min(
      field: string,
      params?: Record<string, unknown>,
    ): Promise<number> {
      const apiParams = { ...params, aggregate: 'min', field } as Record<
        string,
        string | number | boolean
      >;
      return handleRequest(
        async () => {
          const response = await $apiClient.get<{ min: number }>(resourceUrl, {
            params: apiParams,
          });
          return response.min;
        },
        'GET',
        resourceUrl,
        apiParams,
      );
    },

    async avg(
      field: string,
      params?: Record<string, unknown>,
    ): Promise<number> {
      const apiParams = { ...params, aggregate: 'avg', field } as Record<
        string,
        string | number | boolean
      >;
      return handleRequest(
        async () => {
          const response = await $apiClient.get<{ avg: number }>(resourceUrl, {
            params: apiParams,
          });
          return response.avg;
        },
        'GET',
        resourceUrl,
        apiParams,
      );
    },

    async sum(
      field: string,
      params?: Record<string, unknown>,
    ): Promise<number> {
      const apiParams = { ...params, aggregate: 'sum', field } as Record<
        string,
        string | number | boolean
      >;
      return handleRequest(
        async () => {
          const response = await $apiClient.get<{ sum: number }>(resourceUrl, {
            params: apiParams,
          });
          return response.sum;
        },
        'GET',
        resourceUrl,
        apiParams,
      );
    },

    // Relationship methods
    with(relations: string | string[]): QueryBuilder<T> {
      return createQueryBuilder().with(relations);
    },

    // Scope methods
    latest(field = 'created_at'): QueryBuilder<T> {
      return createQueryBuilder().orderBy(field, 'desc');
    },

    oldest(field = 'created_at'): QueryBuilder<T> {
      return createQueryBuilder().orderBy(field, 'asc');
    },
  };
}

/**
 * Convenience function to create a reactive API model with automatic loading
 * This combines the power of useApiModel with automatic reactivity to filter/pagination changes
 */
export function useReactiveApiModel<T = Record<string, unknown>>(
  resourceName: string,
  options: {
    baseUrl?: string;
    filters?: MaybeRefOrGetter<Record<string, string | number | boolean>>;
    page?: Ref<number>;
    limit?: Ref<number>;
    immediate?: boolean;
    debounceMs?: number;
    enableDeduplication?: boolean;
    deduplicationTtl?: number;
  } = {},
) {
  // Create default refs if not provided
  const pageRef = options.page || ref(1);
  const limitRef = options.limit || ref(15);

  const {
    filters,
    immediate = true,
    baseUrl,
    enableDeduplication = true,
    deduplicationTtl = DEFAULT_CACHE_TTL,
  } = options;

  // Create the reactive API model with auto-loading enabled
  const model = useApiModel<T>(resourceName, {
    baseUrl,
    immediate,
    filters,
    page: pageRef,
    limit: limitRef,
    autoLoad: true,
    enableDeduplication,
    deduplicationTtl,
  });

  // Provide convenience methods for pagination
  const nextPage = () => {
    const currentPage = pageRef.value;
    const totalPages = model.meta.value.last_page || 1;
    if (currentPage < totalPages) {
      pageRef.value = currentPage + 1;
    }
  };

  const prevPage = () => {
    const currentPage = pageRef.value;
    if (currentPage > 1) {
      pageRef.value = currentPage - 1;
    }
  };

  const goToPage = (pageNumber: number) => {
    const clampedPage = Math.max(1, pageNumber);
    const maxPage = model.meta.value.last_page || 1;
    pageRef.value = Math.min(clampedPage, maxPage);
  };

  const setLimit = (newLimit: number) => {
    const validLimit = Math.max(1, newLimit);
    limitRef.value = validLimit;
    // Reset to first page when changing limit
    pageRef.value = 1;
  };

  // Computed properties for pagination info (memoized to prevent unnecessary re-renders)
  const hasNextPage = computed(() => {
    const currentPage = model.meta.value.current_page || 1;
    const totalPages = model.meta.value.last_page || 1;
    return currentPage < totalPages;
  });

  const hasPrevPage = computed(() => {
    const currentPage = model.meta.value.current_page || 1;
    return currentPage > 1;
  });

  const totalItems = computed(() => model.meta.value.total || 0);
  const currentPage = computed(() => model.meta.value.current_page || 1);
  const totalPages = computed(() => model.meta.value.last_page || 1);
  const itemsPerPage = computed(
    () => model.meta.value.per_page || limitRef.value,
  );

  return {
    // Core model properties and methods (selectively exposed to avoid over-spreading)
    items: model.items,
    meta: model.meta,
    pending: model.pending,
    error: model.error,
    load: model.load,
    refresh: model.refresh,

    // Basic CRUD operations
    index: model.index,
    show: model.show,
    store: model.store,
    update: model.update,
    destroy: model.destroy,

    // Query builder methods
    query: model.query,
    where: model.where,
    whereIn: model.whereIn,
    orderBy: model.orderBy,

    // Eloquent-style methods
    all: model.all,
    find: model.find,
    findOrFail: model.findOrFail,
    findMany: model.findMany,
    first: model.first,
    firstOrFail: model.firstOrFail,
    create: model.create,
    delete: model.delete,
    count: model.count,
    paginate: model.paginate,

    // Pagination controls
    nextPage,
    prevPage,
    goToPage,
    setLimit,

    // Pagination computed properties
    hasNextPage,
    hasPrevPage,
    totalItems,
    currentPage,
    totalPages,
    itemsPerPage,

    // Expose the reactive refs for external control
    pageRef,
    limitRef,
  };
}
